import React, { useState, useEffect } from "react";
import { useLanguage } from "../i18n/LanguageContext";
import { createPortal } from "react-dom";

/**
 * Floating sticky "Register" shortcut button.
 * Appears at the bottom-right corner after the user has scrolled past the hero.
 * Always-visible CTA so users can register from any point of the course pages.
 *
 * Rendered through a React portal directly into `document.body` so that the
 * `position: fixed` is not broken by an ancestor with a `transform`/`filter`.
 *
 * Props:
 *  - onClick: callback that opens the registration popup
 *  - label: button text (defaults to "Regjistrohu")
 */
const FloatingRegisterButton = ({ onClick, label = "Regjistrohu", testId = "floating-register-btn" }) => {
  const { tx } = useLanguage();
  const [visible, setVisible] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    const onScroll = () => setVisible(window.scrollY > 320);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  if (!mounted) return null;

  return createPortal(
    <>
      <style>{`
        @keyframes floatRegPulse {
          0%, 100% { box-shadow: 0 12px 32px rgba(60,212,225,0.35), 0 0 0 0 rgba(60,212,225,0.55); }
          50%      { box-shadow: 0 12px 32px rgba(60,212,225,0.45), 0 0 0 14px rgba(60,212,225,0); }
        }
        .floating-reg-btn {
          position: fixed;
          right: 22px;
          bottom: 92px;
          z-index: 60;
          display: inline-flex;
          align-items: center;
          gap: 10px;
          padding: 14px 22px;
          border-radius: 999px;
          background: linear-gradient(130deg, #3CD4E1 0%, #1CA3B0 60%, #003F72 100%);
          color: #ffffff;
          font-weight: 700;
          font-size: 14px;
          letter-spacing: 0.01em;
          border: 0;
          cursor: pointer;
          transform: translateY(20px) scale(0.92);
          opacity: 0;
          pointer-events: none;
          transition: transform 0.45s cubic-bezier(0.22,1,0.36,1),
                      opacity 0.35s ease-out;
          animation: floatRegPulse 2.6s ease-in-out infinite;
          font-family: 'Manrope', 'Inter Tight', sans-serif;
        }
        .floating-reg-btn.is-visible {
          transform: translateY(0) scale(1);
          opacity: 1;
          pointer-events: auto;
        }
        .floating-reg-btn:hover {
          transform: translateY(-2px) scale(1.04);
        }
        .floating-reg-btn .icon {
          width: 22px;
          height: 22px;
          border-radius: 50%;
          background: rgba(255,255,255,0.18);
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }
        @media (max-width: 640px) {
          .floating-reg-btn {
            right: 14px;
            bottom: 76px;
            padding: 12px 18px;
            font-size: 13px;
          }
          .floating-reg-btn .label-long { display: none; }
        }
      `}</style>
      <button
        type="button"
        onClick={onClick}
        className={`floating-reg-btn ${visible ? "is-visible" : ""}`}
        aria-label={tx(label)}
        data-testid={testId}
      >
        <span className="icon" aria-hidden="true">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 12h14M13 5l7 7-7 7" />
          </svg>
        </span>
        <span>{tx(label)}</span>
      </button>
    </>,
    document.body
  );
};

export default FloatingRegisterButton;
